import { Routes } from "@/app/routes.ts";
import { useMobileMenuContext } from "@/components/layouts/MobileMenuProvider.tsx";
import { SidebarMenu } from "@/components/layouts/SidebarMenu.tsx";
import { ConversationHistory } from "@/features/conversations/components/conversation-history/ConversationHistory.tsx";
import { Aside, Button, Flex, LinkButton } from "@hopper-ui/components";
import { useEnvironmentVariables, useFeatureFlag, useGetCurrentUserSuspenseQuery, useSharedTranslation, useSidePanelStore, useTracking } from "@workleap-ai/shared";
import { MixpanelEvent } from "@workleap-ai/shared/types";
import { useCallback } from "react";
import { useMatch } from "react-router";

export const Sidebar = () => {
    const { tShared } = useSharedTranslation();
    const { managementAppBaseUrl } = useEnvironmentVariables();
    const { trackEvent } = useTracking();
    const { isMobileMenuOpen } = useMobileMenuContext();
    const { hasFeatureFlag } = useFeatureFlag();
    const { data: { isAdmin, isExecutive, isTeamManager } } = useGetCurrentUserSuspenseQuery();
    const toggleSidePanel = useSidePanelStore(state => state.toggleSidePanel);
    const channelsSidePanelIsOpen = useSidePanelStore(state => state.isTypeOpen("channels"));

    const isPersonalizationActive = useMatch({ path: Routes.Personalization() });
    const isSmartReportsActive = useMatch({ path: Routes.SmartReports(), end: false });
    const canUserAccessSmartReports = hasFeatureFlag("SmartReportsManagement") && (isExecutive || isTeamManager);

    const handleToggleChannelsSidePanel = useCallback(() => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Sidebar",
            Trigger: "Toggle Knowledge Sources"
        });

        toggleSidePanel("channels");
    }, [toggleSidePanel, trackEvent]);

    return (
        <Aside
            top={{ base: "var(--wai-navbar-height)", sm: "0" }}
            position={{ base: "absolute", sm: "relative" }}
            zIndex={{ base: 30, sm: 0 }}
            opacity={{ base: isMobileMenuOpen ? "1" : "0", sm: "1" }}
            transform={{ base: isMobileMenuOpen ? "translateX(0)" : "translateX(-100%)", sm: "none" }}
            gridArea="sidebar"
            UNSAFE_width={{
                base: "100%",
                sm: "var(--wai-sidebar-width)"
            }}
            backgroundColor="neutral-weakest"
            borderRight={{
                base: "none",
                sm: "neutral-weak"
            }}
            UNSAFE_height="calc(100dvh - var(--wai-navbar-height))"
        >
            <Flex
                direction="column"
                gap="inline-md"
                paddingTop="inset-md"
                height="100%"
            >
                <SidebarMenu />
                <Flex
                    padding="inset-sm"
                    borderTop="neutral-weak"
                    borderBottom="neutral-weak"
                    direction="column"
                    gap="inline-xs"
                >
                    <Button
                        variant="ghost-secondary"
                        size="sm"
                        padding="inset-sm"
                        backgroundColorHover="neutral-weakest-hover"
                        backgroundColor={channelsSidePanelIsOpen ? "neutral-weakest-hover" : undefined}
                        justifyContent="flex-start"
                        isFluid
                        onPress={handleToggleChannelsSidePanel}
                    >
                        {tShared("knowledgeSources")}
                    </Button>
                    <LinkButton
                        variant="ghost-secondary"
                        size="sm"
                        padding="inset-sm"
                        backgroundColorHover="neutral-weakest-hover"
                        backgroundColor={isPersonalizationActive ? "neutral-weakest-hover" : undefined}
                        justifyContent="flex-start"
                        isFluid
                        href={Routes.Personalization()}
                    >
                        {tShared("personalize")}
                    </LinkButton>
                    {canUserAccessSmartReports &&
                        <LinkButton
                            variant="ghost-secondary"
                            size="sm"
                            padding="inset-sm"
                            backgroundColorHover="neutral-weakest-hover"
                            backgroundColor={isSmartReportsActive ? "neutral-weakest-hover" : undefined}
                            justifyContent="flex-start"
                            isFluid
                            href={Routes.SmartReports()}
                        >
                            {tShared("smartReports.title")}
                        </LinkButton>
                    }
                    {isAdmin &&
                        <LinkButton
                            variant="ghost-secondary"
                            size="sm"
                            padding="inset-sm"
                            backgroundColorHover="neutral-weakest-hover"
                            justifyContent="flex-start"
                            isFluid
                            isExternal
                            href={`${managementAppBaseUrl}/settings/ai`}
                        >
                            {tShared("workleapAiSettings")}
                        </LinkButton>
                    }
                </Flex>
                <ConversationHistory />
            </Flex>
        </Aside>
    );
};
